import * as babylon from "@babylonjs/core";

import { ThreeDPanelState } from "@/shared/state/visualization/schema/v1";

import { DataServerManager } from "../DataServer/DataServerManager.ts";

import { initScene } from "./assetBuilders/scene.ts";
import {
  BabylonState,
  BabylonTheme,
  Callbacks,
  Vector3Plain,
  ZERO_CAMERA_DELTAS,
} from "./state.ts";
import * as systems from "./systems/index.ts";
import {
  ORBIT_DEFAULT_ALPHA_RAD,
  ORBIT_DEFAULT_BETA_RAD,
  ORBIT_DEFAULT_ZOOM,
} from "./systems/onSetState/updateSceneConfig.ts";

/**
 * Snapshot of the active camera's framing fields, as plain numbers and
 * a `Vector3Plain` target. Read by the mode toggle to seed a
 * third-person spec from a live orbit camera (alpha/beta/radius and the
 * followed entity's data-space position) without leaking Babylon
 * internals across the React boundary.
 */
export interface CameraSnapshot {
  alpha: number;
  beta: number;
  radius: number;
  target: Vector3Plain;
}

/**
 * Pure helper. Returns a data-space snapshot of the active camera's
 * framing, or `undefined` when no camera is built.
 *
 * `target` is the camera's data-space focus, read from
 * `camera.focusPoint` (the authoritative render origin written by
 * `updateCamera` each frame). Not the same as `babylonCamera.target`,
 * which stays at `Vector3.Zero()` for both modes — capturing that
 * would seed `focusPoint = (0,0,0)` on the toggle and place the
 * third-person camera nowhere near the actual content. The plain
 * `Vector3Plain` return keeps the React boundary free of Babylon types.
 */
export function cameraSnapshot(
  state: BabylonState,
): CameraSnapshot | undefined {
  const camera = state.sceneConfig.camera;
  if (!camera) {
    return undefined;
  }
  const target = findDataSpaceTarget(state);
  return {
    alpha: camera.babylonCamera.alpha,
    beta: camera.babylonCamera.beta,
    radius: camera.babylonCamera.radius,
    target: { x: target.x, y: target.y, z: target.z },
  };
}

function findDataSpaceTarget(state: BabylonState): {
  x: number;
  y: number;
  z: number;
} {
  const camera = state.sceneConfig.camera;
  if (!camera) {
    return { x: 0, y: 0, z: 0 };
  }
  // Both modes' `camera.focusPoint` is the data-space focus, written by
  // `updateCamera` each frame. For third-person it's the value the user
  // is panning/elevating to; for orbit it mirrors the tracked entity.
  // Returning a `Vector3Plain` keeps the React boundary free of Babylon
  // types.
  const fp = camera.focusPoint;
  return { x: fp.x, y: fp.y, z: fp.z };
}

/**
 * Returns the orbit camera's initial framing for `target` — same
 * angles and radius `buildOrbitCamera` would apply on a fresh build
 * with the default zoom envelope. Returns `undefined` when no camera
 * is built or the entity's position is unavailable.
 */
export function recenterFraming(
  state: BabylonState,
  target: { id: string; mesh: "mesh" | "path" },
): CameraSnapshot | undefined {
  const camera = state.sceneConfig.camera;
  if (!camera) {
    return undefined;
  }
  const entity = state.entities[target.id];
  if (!entity) {
    return undefined;
  }
  const source =
    target.mesh === "mesh"
      ? entity.position?.current.vector
      : entity.position?.mean;
  if (!source) {
    return undefined;
  }
  const { min, max } = ORBIT_DEFAULT_ZOOM;
  return {
    alpha: ORBIT_DEFAULT_ALPHA_RAD,
    beta: ORBIT_DEFAULT_BETA_RAD,
    radius: min + (max - min) / 5,
    target: { x: source.x, y: source.y, z: source.z },
  };
}

export type Params = {
  dataServer: DataServerManager;
  canvas: HTMLCanvasElement;
  time: bigint;
  theme: BabylonTheme;
  callbacks: Callbacks;
  initialState: ThreeDPanelState;
};

/**
 * State manager for a BabylonJS visualization driven by the VizState and visualization timer.
 *
 * Draws inspiration from the ECS pattern, where systems mutate the long-lived mutable state
 * of entities instead of reducers producing immutable, ephemeral state copies.
 *
 * For more details, see the subpackage README.md.
 */
export class BabylonManager {
  private readonly state: BabylonState;
  disposed = false;

  constructor(params: Params) {
    this.state = initState(params);
  }

  setState(panelState: ThreeDPanelState) {
    if (this.disposed) {
      return;
    }
    systems.onSetState(this.state, panelState);
  }

  setTime(time: bigint) {
    if (this.disposed) {
      return;
    }
    systems.onSetTime(this.state, time);
  }

  setTheme(theme: BabylonTheme) {
    if (this.disposed) {
      return;
    }
    systems.onSetTheme(this.state, theme);
  }

  /**
   * Read-only snapshot of the active camera's framing fields. Returns
   * `undefined` when the manager is disposed or no camera is built yet
   * (e.g. before the first `setState`). The mode toggle calls this to
   * capture a live orbit camera's framing into a third-person spec.
   */
  getCameraSnapshot(): CameraSnapshot | undefined {
    if (this.disposed) {
      return undefined;
    }
    return cameraSnapshot(this.state);
  }

  /**
   * Apply the orbit-init framing for `target` to the live camera.
   * Imperative — does not round-trip through panel state, so
   * (a) consecutive clicks always re-apply even if the framing values
   * haven't changed, and (b) live framing isn't persisted (covered by
   * the broader live-framing-persistence TODO).
   */
  recenter(target: { id: string; mesh: "mesh" | "path" }): void {
    if (this.disposed) {
      return;
    }
    const framing = recenterFraming(this.state, target);
    if (!framing) {
      return;
    }
    const camera = this.state.sceneConfig.camera;
    if (!camera) {
      return;
    }
    camera.babylonCamera.alpha = framing.alpha;
    camera.babylonCamera.beta = framing.beta;
    camera.babylonCamera.radius = framing.radius;
    camera.focusPoint.set(framing.target.x, framing.target.y, framing.target.z);
    this.state.input.cameraDeltas = { ...ZERO_CAMERA_DELTAS };
  }

  /**
   * Stops all scheduled systems, prevents new systems from being scheduled,
   * and disposes all of the state's resources.
   */
  dispose() {
    this.disposed = true;
    const ctx = this.state.context;
    for (const schedule of Object.values(ctx.systemSchedules)) {
      schedule.dispose();
    }
    // Dispose all entities in the scene.
    systems.onSetState(this.state, { data: { entities: {} } });
    // Dispose the scene and engine.
    ctx.scene.dispose();
    ctx.engine.dispose();
    ctx.dataServer.dispose();
  }
}

function initState(params: Params): BabylonState {
  params.callbacks.onLoadingStatusChanged(true);

  const engine = new babylon.Engine(params.canvas);
  engine.renderEvenInBackground = false;
  const scene = initScene(engine, params.theme, params.callbacks);

  const state: BabylonState = {
    entities: {},
    sceneConfig: { entityGeneration: 0 },
    input: {
      cameraDeltas: { ...ZERO_CAMERA_DELTAS },
    },
    context: {
      systemSchedules: {},
      dataServer: params.dataServer,
      callbacks: params.callbacks,
      canvas: params.canvas,
      engine,
      scene,
    },
  };
  systems.onSetState(state, params.initialState);
  systems.onSetTime(state, params.time);
  scheduleSystems(state);
  return state;
}

const RELOAD_STALE_DATA_INTERVAL_MS = 50;

function scheduleSystems(state: BabylonState) {
  const systemSchedules = state.context.systemSchedules;

  const engine = state.context.engine;
  const render = () => {
    systems.render(state);
  };
  engine.runRenderLoop(render);
  systemSchedules["RENDER"] = {
    dispose: () => engine.stopRenderLoop(render),
  };

  const resize = () => {
    systems.resize(state);
  };
  const resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(state.context.canvas);
  systemSchedules["RESIZE"] = {
    dispose: () => resizeObserver.disconnect(),
  };

  const reloadStaleDataInterval = window.setInterval(() => {
    systems.reloadStaleComponents(state);
  }, RELOAD_STALE_DATA_INTERVAL_MS);
  systemSchedules["RELOAD_STALE_DATA"] = {
    dispose: () => window.clearInterval(reloadStaleDataInterval),
  };

  // Third-person input handlers attach for the BabylonManager's
  // lifetime; each handler gates internally on the active camera type
  // and no-ops when an orbit camera is active. Babylon's default
  // `CameraInputsManager` pipeline is uninstalled by
  // `babylonCamera.inputs.clear()` inside `buildThirdPersonCamera`
  // (see `updateSceneConfig`), so these three handlers are the only
  // input path for the third-person camera.
  //
  // Each handler is registered as its own schedule so a synchronous
  // throw during one attach does not leave earlier handlers wired but
  // unrecorded.
  systemSchedules["THIRD_PERSON_ORBIT_INPUT"] = {
    dispose: systems.attachOrbitInput(state),
  };
  systemSchedules["THIRD_PERSON_ZOOM_INPUT"] = {
    dispose: systems.attachZoomInput(state),
  };
  systemSchedules["THIRD_PERSON_PAN_INPUT"] = {
    dispose: systems.attachPanInput(state),
  };
}
